/**
 * 🔍 Shared employee search matchers
 *
 * Two independent boxes above the employees table:
 *
 *   1. matchesEmployeeSearch — the general box:
 *        name (English and Arabic, regardless of the current UI language),
 *        Insurance Number, email, phone number.
 *   2. matchesIdentifier — a dedicated identifier box searching exactly ONE
 *        of Client Code or National ID, chosen by the user. It is one-at-a-
 *        time on purpose: a 5-digit client code is a substring of plenty of
 *        14-digit national IDs, so searching both at once buries the row you
 *        want under false positives.
 *
 * The Infinity Code is searchable in neither: it is an internal identifier
 * that the import refuses to read and the export never emits.
 *
 * Lives here because the admin table, the admin filter utils and the manager
 * page each had their own copy of the filter; they now share this one so the
 * three boxes can never drift apart again.
 *
 * Both page modules are supported: the admin mapper puts the raw record on
 * `fullEmployeeData`, while the manager mapper copies a subset onto it and
 * picks a language-dependent `name`. Reading top-level *and* `fullEmployeeData`
 * covers both shapes.
 */

// The employee mappers fill missing values with the literal string "N/A".
// Matching that would make a search for "a" return every employee with a blank
// field, so placeholders are treated as empty.
const EMPTY_VALUES = new Set(["n/a", "na", "-", "--"]);

const norm = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).toLowerCase().trim();
  return EMPTY_VALUES.has(str) ? "" : str;
};

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

/** Every value the search box looks at, for one employee row. */
export const getEmployeeSearchFields = (employee = {}) => {
  const full = employee.fullEmployeeData || {};
  return [
    // Name, in both languages. The manager mapper's `name` holds only the
    // language-appropriate one, so the other is read off the raw record.
    employee.name,
    employee.arabicName,
    full.sr,
    full.arabicName,
    // Client Code and National ID deliberately absent — they have their own
    // box, see matchesIdentifier below.
    full.insuranceNumber,
    employee.email,
    full.email,
    employee.phone,
    full.phoneNumber,
  ];
};

/**
 * True when `employee` matches `searchTerm`. An empty term matches everything,
 * so callers can use it unconditionally.
 */
export const matchesEmployeeSearch = (employee, searchTerm) => {
  const term = norm(searchTerm);
  if (!term) return true;
  if (!employee) return false;

  if (getEmployeeSearchFields(employee).some((field) => norm(field).includes(term)))
    return true;

  // Phone numbers get typed with spaces, dashes and country codes. Comparing
  // bare digits lets "0100 123 4567" and "+20 100 123 4567" both find the
  // stored "01001234567". Three digits minimum, otherwise almost every row
  // matches a one- or two-digit term.
  const termDigits = digitsOnly(searchTerm);
  if (termDigits.length >= 3) {
    const phoneDigits = [employee.phone, employee.fullEmployeeData?.phoneNumber]
      .map(digitsOnly)
      .filter(Boolean);
    if (phoneDigits.some((phone) => phone.includes(termDigits))) return true;
  }

  return false;
};

/** The identifier the dedicated box is currently searching. */
export const IDENTIFIER_FIELDS = {
  CLIENT_CODE: "clientCode",
  NATIONAL_ID: "nationalId",
};

/**
 * True when the employee's chosen identifier matches `term`. `field` is one of
 * IDENTIFIER_FIELDS — exactly one is searched, never both, so a short Client
 * Code cannot match employees whose National ID merely contains those digits.
 * An empty term matches everything.
 */
export const matchesIdentifier = (
  employee,
  term,
  field = IDENTIFIER_FIELDS.CLIENT_CODE,
) => {
  const needle = norm(term);
  if (!needle) return true;
  if (!employee) return false;

  const full = employee.fullEmployeeData || {};
  const value =
    field === IDENTIFIER_FIELDS.NATIONAL_ID
      ? full.nationalId ?? employee.nationalId
      : full.clientCode ?? employee.clientCode;

  if (norm(value).includes(needle)) return true;

  // National IDs in particular get pasted with spaces or dashes; compare bare
  // digits too. Three digits minimum, same reason as the phone match above.
  const termDigits = digitsOnly(term);
  if (termDigits.length >= 3) {
    const valueDigits = digitsOnly(value);
    if (valueDigits && valueDigits.includes(termDigits)) return true;
  }
  return false;
};

export default matchesEmployeeSearch;
